import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import OfferCard from './OfferCard';
import type { OfferDTO } from '../user/on-sale/types';
import api from '../../api/axios';

const SkinCarousel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const positions = [-1, 0, 1];
  const [isAnimating, setIsAnimating] = useState(false);
  const [offers, setOffers] = useState<OfferDTO[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await api.get('/landing-page/offers');
        const data = res.data.content ?? res.data;
        setOffers(Array.isArray(data) ? data : []);
      } catch (error) {
        // Silent error
      }
    };
    fetchOffers();
  }, []);

  const handleNext = useCallback(() => {
    if (!isAnimating) setActiveIndex((prev) => prev + 1);
  }, [isAnimating]);

  const handlePrev = useCallback(() => {
    if (!isAnimating) setActiveIndex((prev) => prev - 1);
  }, [isAnimating]);

  useEffect(() => {
    if (offers.length === 0) return;
    timerRef.current = setInterval(() => {
      handleNext();
    }, 5000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [handleNext, offers.length, activeIndex]);

  if (offers.length === 0) {
    return null;
  }

  return (
      <div className="relative w-full h-[550px] md:h-[600px] flex items-center justify-center [perspective:2000px] overflow-hidden md:overflow-visible">

        {/* Navigation Controls */}
        <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between z-[60] pointer-events-none pl-2 pr-0 lg:pr-10 lg:pl-4">
          <div className="pointer-events-auto">
            <IconButton
                onClick={handlePrev}
                className="bg-white/90 dark:bg-neutral-900/90 border border-neutral-200 dark:border-white/10 text-light-text dark:text-dark-text shadow-xl"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  color: 'inherit',
                  '.dark &': { bgcolor: 'rgba(23, 23, 23, 0.9)' },
                  '&:hover': {
                    bgcolor: '#3b82f6',
                    color: 'white',
                    borderColor: '#3b82f6',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out',
                  width: { xs: 44 },
                  height: { xs: 44},
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
            >
              <ChevronLeft fontSize={isMobile ? "medium" : "large"} />
            </IconButton>
          </div>

          <div className="pointer-events-auto">
            <IconButton
                onClick={handleNext}
                className="bg-white/90 dark:bg-neutral-900/90 border border-neutral-200 dark:border-white/10 text-light-text dark:text-dark-text shadow-xl"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  color: 'inherit',
                  '.dark &': { bgcolor: 'rgba(23, 23, 23, 0.9)' },
                  '&:hover': {
                    bgcolor: '#3b82f6',
                    color: 'white',
                    borderColor: '#3b82f6',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out',
                  width: { xs: 44 },
                  height: { xs: 44},
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
            >
              <ChevronRight fontSize={isMobile ? "medium" : "large"} />
            </IconButton>
          </div>
        </div>

        {/* Carousel Content */}
        <div className="relative w-full h-full flex items-center justify-center pointer-events-none">
          <AnimatePresence initial={false} onExitComplete={() => setIsAnimating(false)}>
            {positions.map((offset) => {
              const virtualIndex = activeIndex + offset;
              const itemIndex = ((virtualIndex % offers.length) + offers.length) % offers.length;
              const offer = offers[itemIndex];

              if (!offer) return null;

              const isCenter = offset === 0;
              const mobileOpacity = isCenter ? 1 : 0;
              const mobileX = offset * 350;

              return (
                  <motion.div
                      key={`virtual-${virtualIndex}`}
                      onAnimationStart={() => setIsAnimating(true)}
                      onAnimationComplete={() => setIsAnimating(false)}
                      initial={{
                        opacity: 0,
                        x: isMobile ? (offset > 0 ? 350 : -350) : (offset > 0 ? 400 : -400),
                        rotateY: isMobile ? 0 : (offset > 0 ? -20 : 20),
                        scale: isMobile ? 0.9 : 0.8,
                        z: isMobile ? 0 : -200
                      }}
                      animate={{
                        opacity: isMobile ? mobileOpacity : (isCenter ? 1 : 0.4),
                        scale: isCenter ? 1 : (isMobile ? 0.9 : 0.85),
                        x: isMobile ? mobileX : offset * 310,
                        rotateY: isCenter ? 0 : (isMobile ? 0 : offset * -20),
                        z: isCenter ? 0 : (isMobile ? 0 : -250),
                        zIndex: 10 - Math.abs(offset),
                      }}
                      exit={{
                        opacity: 0,
                        x: isMobile ? (offset === 0 ? (activeIndex > virtualIndex ? 350 : -350) : -mobileX) : (offset === 0 ? (activeIndex > virtualIndex ? 500 : -500) : (offset > 0 ? -400 : 400)),
                        transition: { duration: 0.3 }
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 90,
                        damping: 20,
                        mass: 1
                      }}
                      style={{
                        position: 'absolute',
                        width: isMobile ? '280px' : '340px',
                        transformStyle: 'preserve-3d',
                        backfaceVisibility: 'hidden',
                        WebkitFontSmoothing: 'antialiased',
                        pointerEvents: isCenter ? 'auto' : 'none',
                      }}
                  >
                    <div
                        className={`relative ${isCenter ? 'drop-shadow-[0_0_50px_rgba(59,130,246,0.2)]' : ''}`}
                        style={{
                          transform: (isCenter && !isAnimating) ? 'none' : 'translateZ(0)',
                          willChange: 'transform, opacity'
                        }}
                    >
                      <OfferCard offer={offer} />
                      {isCenter && (
                          <div className="absolute -bottom-12 left-0 right-0 h-24 bg-gradient-to-t from-blue-500/15 to-transparent blur-3xl -z-10" />
                      )}
                    </div>
                  </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
  );
};

export default SkinCarousel;